/**
 * useConversationEvents — Spec 66 Phase E + Spec 79 M1.5
 *
 * Subscribes to `/api/v1/conversations/:id/live` over WebSocket.
 * REST message fetching remains the durable backfill/fallback path.
 *
 * Handles:
 *  - Token-by-token assistant message streaming (message.delta)
 *  - Durable message delivery (message.appended, message.finalized)
 *  - Typing indicators (typing.start, typing.stop)
 *  - Presence updates (presence.update)
 *  - Agent thinking state (turn.started, turn.completed)
 *  - Session expiry (session.expired) — terminal, no reconnect
 *  - Slow-consumer error (error code='slow_consumer') — reconnect with backoff
 *  - Exponential backoff reconnection (1s → 2s → 4s → 8s, cap 30s)
 *  - Last-Event-ID resume via localStorage + connection query
 *  - Orchestrator streaming events (ephemeral, not persisted to messages)
 *
 * The legacy SSE route is still available for gated surfaces, but the main
 * conversation UI does not call it as a fallback because disabled orgs receive
 * a 404. Failed live setup degrades to REST refresh instead.
 */
import type { ConversationMemberRef, ConversationRecipient } from '@confluent/shared/lib/conversation-addressing';
import type { MessageAttachment } from '@confluent/shared/lib/message-attachment';
import { useState, useEffect, useRef, useCallback } from 'react';
import { API_V1_BASE, getAuthToken as getSharedAuthToken } from '../services/api/session.js';
import { useAuthStore } from '../store/index.js';
import type {
  ConversationEventEnvelope,
  MessageAppendedPayload,
  MessageDeltaPayload,
  MessageFinalizedPayload,
  TurnStartedPayload,
  TurnCompletedPayload,
  TypingStartPayload,
  TypingStopPayload,
  PresenceUpdatePayload,
  SessionExpiredPayload,
  ErrorPayload,
} from '@confluent/shared/lib/event-stream/event-envelope';
import {
  type OrchestratorToolCallState,
  type OrchestratorTurnState,
  applyOrchestratorEventToTurn,
  makeInitialOrchestratorTurn,
} from './useConversationEvents.orchestrator.js';
import type { ConversationMessagePart } from '@confluent/shared/lib/conversation-interactions';
export type { OrchestratorToolCallState, OrchestratorTurnState };

// ─── Public types ─────────────────────────────────────────────────────────────

export interface ConversationMessageView {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  parts?: ConversationMessagePart[];
  /** The member who wrote it, and the members it is for (conversation-members spec). */
  author?: ConversationMemberRef | null;
  to?: ConversationRecipient[];
  /** The files a person attached (only their owner can download them). */
  attachments?: MessageAttachment[];
  createdAt: string;
}

export interface ConversationEventsState {
  /** Durable messages. Sorted asc by createdAt. */
  messages: ConversationMessageView[];
  /**
   * Currently-streaming assistant turn delta.
   * null when no turn in progress.
   */
  streamingMessage: { messageId: string; content: string; turnId: string | null } | null;
  /** Other users currently typing (excludes self). */
  typingUsers: Array<{ userId: string; displayName: string }>;
  /** People viewing the detail page (from presence.update). */
  viewers: Array<{ userId: string; displayName: string; viewingSince: string }>;
  /** True while engine is executing a turn (turn.started but not turn.completed). */
  agentThinking: boolean;
  /** Connection status. */
  status: 'idle' | 'connecting' | 'open' | 'reconnecting' | 'error';
  /** Active live transport. REST backfill remains the durable fallback. */
  transport: 'websocket' | 'rest';
  /**
   * Set when session.expired arrived. Terminal — the drawer/detail
   * should surface this and stop reconnecting.
   */
  sessionExpired: { reason: string; instruction: string } | null;
  /** Ephemeral orchestrator turn state. Drives the live turn in the chat thread (the running message's reasoning, tool calls and text). */
  orchestratorTurn: OrchestratorTurnState;
  /**
   * How many times a message's recipients changed (`recipients.updated`: a member took it up, answered or failed it,
   * or the engine gave it back): the stream reads its messages again on each, since only the server knows the statuses.
   */
  recipientsRevision: number;
}

export interface UseConversationEventsOptions {
  /** null / undefined disables the hook. */
  conversationId: string | number | null | undefined;
  /** Default true. Pass false to disable (e.g. feature-flag gate at call site). */
  enabled?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPING_AUTO_EVICT_MS = 5_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const LAST_EVENT_KEY = (id: string) => `conv-last-event-${id}`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCurrentUserId(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

function nextBackoff(prev: number): number {
  return Math.min(prev * 2, BACKOFF_MAX_MS);
}

function buildConversationWebSocketUrl(convId: string, lastEventId: string | null): string {
  const base = new URL(API_V1_BASE);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = `${base.pathname.replace(/\/$/, '')}/conversations/${convId}/live`;
  base.search = '';
  if (lastEventId) base.searchParams.set('last_event_id', lastEventId);
  return base.toString();
}

function makeInitialState(): ConversationEventsState {
  return {
    messages: [],
    streamingMessage: null,
    typingUsers: [],
    viewers: [],
    agentThinking: false,
    status: 'idle',
    transport: 'rest',
    sessionExpired: null,
    orchestratorTurn: makeInitialOrchestratorTurn(),
    recipientsRevision: 0,
  };
}

function deriveTurnIdFromStreamingMessageId(messageId: string): string | null {
  return messageId.endsWith('-msg') ? messageId.slice(0, -'-msg'.length) : null;
}

/**
 * Merges a `message.finalized` payload into the durable message list.
 *
 * A message can already be present because `message.appended` arrived first with
 * partial content and no parts; the finalized payload is authoritative, so it
 * REPLACES `content` and `parts` in place (cards only exist on the finalized
 * message). Otherwise the message is appended and the list re-sorted.
 */
export function applyFinalizedMessage(
  messages: ConversationMessageView[],
  payload: MessageFinalizedPayload,
): ConversationMessageView[] {
  const alreadyPresent = messages.some((m) => m.id === payload.messageId);
  if (alreadyPresent) {
    return messages.map((m) =>
      m.id === payload.messageId ? { ...m, content: payload.content, parts: payload.parts ?? m.parts } : m,
    );
  }
  return [
    ...messages,
    {
      id: payload.messageId,
      role: payload.role as ConversationMessageView['role'],
      content: payload.content,
      parts: payload.parts ?? [],
      createdAt: payload.finalizedAt,
    } satisfies ConversationMessageView,
  ].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useConversationEvents(opts: UseConversationEventsOptions): ConversationEventsState {
  const { conversationId, enabled = true } = opts;
  const [state, setState] = useState<ConversationEventsState>(makeInitialState);

  const wsRef = useRef<WebSocket | null>(null);
  // Timer refs for typing auto-eviction.
  const typingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Reconnect backoff state.
  const backoffRef = useRef<number>(BACKOFF_BASE_MS);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Terminal flag — set on session.expired; stops reconnect loop.
  const terminalRef = useRef<boolean>(false);

  const conversationIdStr = conversationId != null ? String(conversationId) : null;

  // ------------------------------------------------------------------
  // Event processing (pure state updater — no I/O)
  // ------------------------------------------------------------------

  const processEnvelope = useCallback(
    (envelope: ConversationEventEnvelope) => {
      const currentUserId = getCurrentUserId();

      setState((prev) => {
        switch (envelope.type) {
          case 'message.appended': {
            const p = envelope.payload as MessageAppendedPayload;

            // Synthetic orchestrator streaming events have ids like "orchestrator:${eventType}:${messageId}".
            // These are transient — drive the streaming UX, NOT the persisted message list.
            const isOrchestratorEvent = p.role === 'tool' && p.messageId.startsWith('orchestrator:');
            if (isOrchestratorEvent) {
              const nextTurn = applyOrchestratorEventToTurn(prev.orchestratorTurn, p.content);
              return nextTurn !== prev.orchestratorTurn ? { ...prev, orchestratorTurn: nextTurn } : prev;
            }

            // Deduplicate by id.
            if (prev.messages.some((m) => m.id === p.messageId)) return prev;
            const next: ConversationMessageView = {
              id: p.messageId,
              role: p.role,
              content: p.content,
              parts: p.parts ?? [],
              ...(p.author !== undefined ? { author: p.author } : {}),
              ...(p.to ? { to: p.to } : {}),
              ...(p.attachments?.length ? { attachments: p.attachments } : {}),
              createdAt: p.createdAt,
            };
            const msgs = [...prev.messages, next].sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            );
            return { ...prev, messages: msgs };
          }

          case 'message.delta': {
            const p = envelope.payload as MessageDeltaPayload;
            // The terminal marker retires the streaming bubble; `message.finalized` supplies the
            // durable message a moment later. Clearing here rather than waiting for finalized to
            // match on content matters because the persisted text has had its output contracts
            // stripped, so it no longer equals what was streamed and the bubble would survive
            // alongside the real answer.
            if (p.isLast) {
              return prev.streamingMessage?.messageId === p.messageId ? { ...prev, streamingMessage: null } : prev;
            }
            // Find existing turn ID from the current streaming message if present.
            const currentTurnId = prev.streamingMessage?.turnId ?? deriveTurnIdFromStreamingMessageId(p.messageId);
            const prevContent =
              prev.streamingMessage != null && prev.streamingMessage.messageId === p.messageId
                ? prev.streamingMessage.content
                : '';
            return {
              ...prev,
              streamingMessage: {
                messageId: p.messageId,
                content: prevContent + p.delta,
                turnId: currentTurnId,
              },
            };
          }

          case 'message.finalized': {
            const p = envelope.payload as MessageFinalizedPayload;
            const msgs = applyFinalizedMessage(prev.messages, p);

            // Clear streaming message if it matches this finalized message.
            const streaming =
              prev.streamingMessage?.messageId === p.messageId ||
              (prev.streamingMessage != null && prev.streamingMessage.content === p.content)
                ? null
                : prev.streamingMessage;

            return { ...prev, messages: msgs, streamingMessage: streaming };
          }

          case 'turn.started': {
            const p = envelope.payload as TurnStartedPayload;
            // Store turnId in the streaming message if one is already in flight,
            // otherwise just set agentThinking.
            const streaming = prev.streamingMessage ? { ...prev.streamingMessage, turnId: p.turnId } : null;
            return { ...prev, agentThinking: true, streamingMessage: streaming };
          }

          case 'recipients.updated':
            return { ...prev, recipientsRevision: prev.recipientsRevision + 1 };

          case 'turn.completed': {
            const p = envelope.payload as TurnCompletedPayload;
            // Defensively clear streamingMessage if it was for this turn.
            const streaming = prev.streamingMessage?.turnId === p.turnId ? null : prev.streamingMessage;
            return { ...prev, agentThinking: false, streamingMessage: streaming };
          }

          case 'typing.start': {
            const p = envelope.payload as TypingStartPayload;
            // Skip self.
            if (currentUserId !== null && String(p.userId) === String(currentUserId)) return prev;
            const already = prev.typingUsers.some((u) => u.userId === p.userId);
            if (already) return prev;
            return {
              ...prev,
              typingUsers: [...prev.typingUsers, { userId: p.userId, displayName: p.displayName }],
            };
          }

          case 'typing.stop': {
            const p = envelope.payload as TypingStopPayload;
            return {
              ...prev,
              typingUsers: prev.typingUsers.filter((u) => u.userId !== p.userId),
            };
          }

          case 'presence.update': {
            const p = envelope.payload as PresenceUpdatePayload;
            return { ...prev, viewers: p.viewers };
          }

          case 'session.expired': {
            const p = envelope.payload as SessionExpiredPayload;
            terminalRef.current = true;
            return {
              ...prev,
              sessionExpired: { reason: p.reason, instruction: p.instruction },
              streamingMessage: null,
              agentThinking: false,
              orchestratorTurn: makeInitialOrchestratorTurn(),
              status: 'error',
            };
          }

          case 'error': {
            const p = envelope.payload as ErrorPayload;
            if (p.code === 'slow_consumer') {
              // Trigger reconnect after 1s (handled in the stream loop).
              return { ...prev, status: 'reconnecting' };
            }
            return { ...prev, status: 'error' };
          }

          // Unhandled event types (member.joined, member.left, attachment.*) — no-op.
          default:
            return prev;
        }
      });
    },
    [], // no deps — reads currentUserId from localStorage on each call
  );

  // ------------------------------------------------------------------
  // Typing auto-eviction
  // ------------------------------------------------------------------

  const scheduleTypingEviction = useCallback((userId: string) => {
    // Clear existing timer.
    const existing = typingTimersRef.current.get(userId);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      typingTimersRef.current.delete(userId);
      setState((prev) => ({
        ...prev,
        typingUsers: prev.typingUsers.filter((u) => u.userId !== userId),
      }));
    }, TYPING_AUTO_EVICT_MS);

    typingTimersRef.current.set(userId, timer);
  }, []);

  // ------------------------------------------------------------------
  // Connect — opens WS for live updates; REST queries remain the fallback
  // ------------------------------------------------------------------

  const connectWebSocket = useCallback(
    (convId: string) => {
      if (!enabled || terminalRef.current) return;
      if (typeof WebSocket === 'undefined') {
        setState((prev) => ({ ...prev, status: 'error', transport: 'rest' }));
        return;
      }

      const token = getSharedAuthToken();
      const lastEventId = localStorage.getItem(LAST_EVENT_KEY(convId));
      const url = buildConversationWebSocketUrl(convId, lastEventId);
      let opened = false;

      setState((prev) => ({
        ...prev,
        status: prev.status === 'reconnecting' ? 'reconnecting' : 'connecting',
        transport: 'websocket',
      }));

      const ws = token ? new WebSocket(url, ['confluent.conversation.v1', `bearer.${token}`]) : new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        opened = true;
        backoffRef.current = BACKOFF_BASE_MS;
        setState((prev) => ({ ...prev, status: 'open' }));
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(String(event.data)) as ConversationEventEnvelope | { type?: string };
          if (parsed.type === 'connection.ready' || parsed.type === 'pong') return;
          if (typeof (parsed as ConversationEventEnvelope).id === 'string') {
            localStorage.setItem(LAST_EVENT_KEY(convId), (parsed as ConversationEventEnvelope).id);
          }
          if (parsed.type === 'typing.start') {
            scheduleTypingEviction(((parsed as ConversationEventEnvelope).payload as { userId: string }).userId);
          }
          if (parsed.type === 'typing.stop') {
            const userId = ((parsed as ConversationEventEnvelope).payload as { userId: string }).userId;
            const existing = typingTimersRef.current.get(userId);
            if (existing) {
              clearTimeout(existing);
              typingTimersRef.current.delete(userId);
            }
          }
          processEnvelope(parsed as ConversationEventEnvelope);
        } catch {
          // Ignore malformed frames.
        }
      };

      ws.onerror = () => {
        // The browser will emit a close event after a failed handshake. Calling
        // close() while CONNECTING creates a noisy "closed before established"
        // console error and can mask the real close path.
      };

      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        if (terminalRef.current) return;

        if (!opened) {
          if (lastEventId) {
            localStorage.removeItem(LAST_EVENT_KEY(convId));
            setState((prev) => ({ ...prev, status: 'reconnecting', transport: 'websocket' }));
            reconnectTimerRef.current = setTimeout(() => {
              if (!terminalRef.current) connectWebSocket(convId);
            }, BACKOFF_BASE_MS);
            return;
          }
          setState((prev) => ({ ...prev, status: 'error', transport: 'rest' }));
          return;
        }

        setState((prev) => ({ ...prev, status: 'reconnecting' }));
        const delay = backoffRef.current;
        backoffRef.current = nextBackoff(delay);
        reconnectTimerRef.current = setTimeout(() => {
          if (!terminalRef.current) connectWebSocket(convId);
        }, delay);
      };
    },
    [enabled, processEnvelope, scheduleTypingEviction],
  );

  // ------------------------------------------------------------------
  // Effect: connect / disconnect when conversationId or enabled changes
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!conversationIdStr || !enabled) {
      setState(makeInitialState);
      return;
    }

    terminalRef.current = false;
    backoffRef.current = BACKOFF_BASE_MS;

    connectWebSocket(conversationIdStr);

    return () => {
      // Cleanup: close live transport, clear reconnect timer, clear typing timers.
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.onopen = () => ws.close(1000, 'cleanup');
        } else if (ws.readyState === WebSocket.OPEN) {
          ws.close(1000, 'cleanup');
        }
      }

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      for (const timer of typingTimersRef.current.values()) {
        clearTimeout(timer);
      }
      typingTimersRef.current.clear();

      setState(makeInitialState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationIdStr, enabled, connectWebSocket]);

  return state;
}
